'use client';

import { Alert, AlertDescription } from '@cortex/ui';

import { ConfirmationQuestion } from './ConfirmationQuestion';
import { MultiSelectQuestion } from './MultiSelectQuestion';
import { SingleSelectQuestion } from './SingleSelectQuestion';
import { SummaryConfirmation } from './SummaryConfirmation';
import { TextQuestion } from './TextQuestion';
import type {
  ConfirmationQuestionProps,
  MultiSelectQuestionProps,
  QuestionRendererProps,
  SingleSelectQuestionProps,
  SummaryConfirmationProps,
  TextQuestionProps,
} from './types';

/**
 * Routes question types to the appropriate component
 */
export function QuestionRenderer({
  questionType,
  data,
  onAnswer,
}: QuestionRendererProps) {
  switch (questionType) {
    case 'ask_single_select':
      return (
        <SingleSelectQuestion
          {...(data as SingleSelectQuestionProps)}
          onSelect={onAnswer}
        />
      );

    case 'ask_multi_select':
      return (
        <MultiSelectQuestion
          {...(data as MultiSelectQuestionProps)}
          onSubmit={(selections, other) => onAnswer({ selections, other })}
        />
      );

    case 'ask_confirmation':
      return (
        <ConfirmationQuestion
          {...(data as ConfirmationQuestionProps)}
          onConfirm={() => onAnswer({ confirmed: true })}
          onDecline={() => onAnswer({ confirmed: false })}
        />
      );

    case 'ask_text':
      return (
        <TextQuestion {...(data as TextQuestionProps)} onSubmit={onAnswer} />
      );

    case 'present_summary':
      return (
        <SummaryConfirmation
          {...(data as SummaryConfirmationProps)}
          onConfirm={() => onAnswer({ action: 'confirm' })}
          onEdit={() => onAnswer({ action: 'edit' })}
        />
      );

    default:
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Unknown question type: {questionType}
          </AlertDescription>
        </Alert>
      );
  }
}
